/* eslint-disable import/no-cycle */
import { useStore } from '@/store';
import { ActionResult } from '@/composables/billing/billingApi';

/**
 * Handles the post-action UX uniformly across the billing module:
 *
 *  - 'completed' + requiresAuthRefresh=true → window.location.reload()
 *    (D3 — actions structurelles)
 *  - 'completed' (no refresh) → refetch overview silently
 *  - 'failed' → surface error via callback / toast
 *  - 'action_required' / 'pending_shopify_approval' → returned for the caller
 *    to handle (3DS Drop-in / Shopify redirect)
 *
 * Callers usually:
 *
 *   const handle = useStructuralReload();
 *   const result = await cancelPlan({ reason });
 *   await handle(result, { onFailure: (err) => showToastError(err) });
 */
export function useStructuralReload() {
  const store = useStore();

  return async function handle(
    result: ActionResult,
    opts: {
      onFailure?: (error: string) => void;
      onActionRequired?: (action: Record<string, any>) => void;
      onShopifyApproval?: (confirmationUrl: string, chargeId: string) => void;
      reloadDelayMs?: number;
    } = {},
  ): Promise<ActionResult> {
    switch (result.status) {
      case 'completed':
        if (result.requiresAuthRefresh) {
          // Tiny delay so the user sees the success state for a frame before
          // the page reloads (also gives time to a toast to appear).
          await new Promise((r) => setTimeout(r, opts.reloadDelayMs ?? 250));
          window.location.reload();
        } else {
          await store.dispatch('billing/refetchOverview');
        }
        break;

      case 'failed':
        opts.onFailure?.(result.error);
        break;

      case 'action_required':
        opts.onActionRequired?.(result.action);
        break;

      case 'pending_shopify_approval':
        opts.onShopifyApproval?.(result.confirmationUrl, result.chargeId);
        break;

      default:
        // Exhaustive switch — TypeScript flags missing cases.
        break;
    }
    return result;
  };
}
